/**
 * Money as the trackers display it, exactly.
 *
 * The API sends receivables, payments and contract values as DECIMAL(19,4)
 * STRINGS ("1250.5000") — summed server-side as integers, never as floats. This
 * file only ever formats one such value for a person; it never adds two (that
 * is the API's job, and a float sum here would undo it). Rounding to cents is
 * half away from zero, on the digits, so "0.0050" reads $0.01 — not whatever
 * the nearest binary double happens to round to.
 *
 * Pure: no React, no MUI.
 */

const DECIMAL_RE = /^(-)?(\d*)(?:\.(\d*))?$/;

/** A decimal string (or a number from an older answer) as integer cents, or null. */
export function toCents(value: unknown): bigint | null {
  if (value === null || value === undefined || value === "") return null;
  const text = typeof value === "number" ? (Number.isFinite(value) ? String(value) : "") : String(value).trim();
  const match = DECIMAL_RE.exec(text);
  if (!match || (match[2] === "" && (match[3] ?? "") === "")) return null;
  const whole = BigInt(match[2] || "0");
  const fraction = (match[3] ?? "").padEnd(3, "0");
  let cents = whole * 100n + BigInt(fraction.slice(0, 2));
  // Half away from zero on the third place (and beyond: "0.00500" is half).
  if (Number(fraction[2]) >= 5) cents += 1n;
  return match[1] === "-" ? -cents : cents;
}

/** "$1,250.50" — or the fallback ("—") when there is no amount. */
export function formatMoney(value: unknown, fallback = "—"): string {
  const cents = toCents(value);
  if (cents === null) return fallback;
  const negative = cents < 0n;
  const abs = negative ? -cents : cents;
  const whole = (abs / 100n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  const fraction = (abs % 100n).toString().padStart(2, "0");
  return `${negative ? "-" : ""}$${whole}.${fraction}`;
}

/**
 * The amount at the API's four places, as an integer (10^-4 units), or null —
 * for COMPARING, never for display. A balance of $0.0001 is owed to the API,
 * and a check made at cents would call it nothing.
 */
export function toUnits(value: unknown): bigint | null {
  if (value === null || value === undefined || value === "") return null;
  const text = typeof value === "number" ? (Number.isFinite(value) ? String(value) : "") : String(value).trim();
  const match = DECIMAL_RE.exec(text);
  if (!match || (match[2] === "" && (match[3] ?? "") === "")) return null;
  const units = BigInt(match[2] || "0") * 10_000n + BigInt((match[3] ?? "").padEnd(4, "0").slice(0, 4));
  return match[1] === "-" ? -units : units;
}

/** True when the amount is more than zero, at the API's four places (its "owed" test). */
export const isPositive = (value: unknown): boolean => (toUnits(value) ?? 0n) > 0n;

/**
 * A YYYY-MM-DD day as a person reads it ("Mar 1, 2026"), formatted from its
 * parts in UTC — so it never shows the day before for anyone west of UTC
 * (billing/lib/dates.ts on the API says why).
 */
export function formatDay(day: string | null | undefined, fallback = "—"): string {
  if (!day) return fallback;
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(day);
  if (!match) return fallback;
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });
}
